import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "emailInput",
        "passwordField",
        "forgotLink",
        "intentStep",
        "confirmStep",
        "successStep",
        "intentButton",
        "confirmButton",
        "confirmationCode",
        "newPassword",
        "newPasswordConfirmation",
        "message",
    ]

    static values = {
        intentUrl:      String,
        confirmUrl:     String,
        errorMessage:   { type: String, default: "Užpildykite visus laukus." },
        genericError:   { type: String, default: "Įvyko klaida, bandykite dar kartą." },
        emailRequired:  { type: String, default: "Prašome įvesti el. pašto adresą." },
    }

    showIntent(e) {
        e.preventDefault()

        const email = this.emailInputTarget.value.trim()
        if (!email) {
            this.emailInputTarget.style.border = "2px solid #dc3545"
            this.emailInputTarget.style.backgroundColor = "#ffe6e6"
            this.emailInputTarget.focus()
            this.showMessage(this.emailRequiredValue, "error")

            this.emailInputTarget.addEventListener("input", () => {
                this.emailInputTarget.style.border = ""
                this.emailInputTarget.style.backgroundColor = ""
                this.clearMessage()
            }, { once: true })

            return
        }

        this.emailInputTarget.style.border = ""
        this.emailInputTarget.style.backgroundColor = ""
        this.clearMessage()

        this.passwordFieldTarget.classList.add("d-none")
        this.forgotLinkTarget.classList.add("d-none")
        this.intentStepTarget.classList.remove("d-none")
    }

    async submitIntent(e) {
        e.preventDefault()

        const email = this.emailInputTarget.value.trim()
        if (!email) {
            this.showMessage(this.errorMessageValue, "error")
            return
        }

        this.setLoading(this.intentButtonTarget, true)
        this.clearMessage()

        try {
            const res  = await this.post(this.intentUrlValue, { existing_email: email })
            const json = await res.json()

            if (res.ok) {
                this.intentStepTarget.classList.add("d-none")
                this.confirmStepTarget.classList.remove("d-none")
                this.showMessage(json.data?.message ?? json.message, "success")
                this.confirmationCodeTarget.focus()
            } else {
                this.showMessage(this.extractError(json), "error")
            }
        } catch {
            this.showMessage(this.genericErrorValue, "error")
        } finally {
            this.setLoading(this.intentButtonTarget, false)
        }
    }

    async submitConfirm(e) {
        e.preventDefault()

        const email        = this.emailInputTarget.value.trim()
        const code         = this.confirmationCodeTarget.value.trim()
        const password     = this.newPasswordTarget.value
        const confirmation = this.newPasswordConfirmationTarget.value

        if (!code || !password || !confirmation) {
            this.showMessage(this.errorMessageValue, "error")
            return
        }
        if (password !== confirmation) {
            this.showMessage(this.errorMessageValue, "error")
            return
        }

        this.setLoading(this.confirmButtonTarget, true)
        this.clearMessage()

        try {
            const res  = await this.post(this.confirmUrlValue, {
                existing_email:            email,
                confirmation_code:         code,
                new_password:              password,
                new_password_confirmation: confirmation,
            })
            const json = await res.json()

            if (res.ok) {
                this.confirmStepTarget.classList.add("d-none")
                this.successStepTarget.classList.remove("d-none")
                this.clearMessage()
            } else {
                this.showMessage(this.extractError(json), "error")
            }
        } catch {
            this.showMessage(this.genericErrorValue, "error")
        } finally {
            this.setLoading(this.confirmButtonTarget, false)
        }
    }

    cancel(e) {
        e.preventDefault()
        this.resetToLogin()
    }

    backToLogin(e) {
        e.preventDefault()
        this.resetToLogin()
        this.clearFields()
    }

    resetToLogin() {
        this.intentStepTarget.classList.add("d-none")
        this.confirmStepTarget.classList.add("d-none")
        this.successStepTarget.classList.add("d-none")
        this.passwordFieldTarget.classList.remove("d-none")
        this.forgotLinkTarget.classList.remove("d-none")
        this.clearMessage()
    }

    clearFields() {
        if (this.hasConfirmationCodeTarget)        this.confirmationCodeTarget.value = ""
        if (this.hasNewPasswordTarget)             this.newPasswordTarget.value = ""
        if (this.hasNewPasswordConfirmationTarget) this.newPasswordConfirmationTarget.value = ""
    }

    showMessage(text, type = "info") {
        if (!this.hasMessageTarget) return
        this.messageTarget.textContent = text
        this.messageTarget.className   = `forgot-password-message mt-3 ${type === "error" ? "text-error" : "text-success"}`
        this.messageTarget.classList.remove("d-none")

        if (type === "error") {
            this.messageTarget.style.color = "#ed443a"
        } else if (type === "success") {
            this.messageTarget.style.color = "#589e48"
        } else {
            this.messageTarget.style.color = ""
        }
    }

    clearMessage() {
        if (!this.hasMessageTarget) return
        this.messageTarget.textContent = ""
        this.messageTarget.classList.add("d-none")
    }

    setLoading(button, isLoading) {
        button.disabled = isLoading
        button.classList.toggle("loading", isLoading)
    }

    extractError(json) {
        const errors = json.data?.errors
        if (errors) {
            const first = Object.values(errors).flat()[0]
            if (first) return first
        }
        return json.data?.message ?? json.message ?? this.genericErrorValue
    }

    async post(url, body) {
        return fetch(url, {
            method:      "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        })
    }
}
